/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useMemo } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { ProprietaryWatermark } from '@/components/security/ProprietaryWatermark';

/**
 * Route guards & router-level bridges.
 * Extracted from App.js (Code Quality refactor) so that App.js only wires providers.
 */

const FullScreenLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

/** Separate component: the `state` object is built once per redirect, not per parent render. */
const RedirectToWelcome = ({ from }) => {
  const state = useMemo(() => ({ from }), [from]);
  return <Navigate to="/welcome" replace state={state} />;
};

export const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullScreenLoader />;
  if (!user) return <RedirectToWelcome from={location} />;
  return children;
};

export const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (user) {
    const target = user.role === 'patient' ? '/telemedicine' : '/feed';
    return <Navigate to={target} replace />;
  }
  return children;
};

// Admin area: proprietary watermark painted behind every administration / configuration panel
export const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user?.is_admin) return <Navigate to="/feed" replace />;
  return (
    <div className="relative isolate" data-testid="admin-protected-area">
      <ProprietaryWatermark />
      {children}
    </div>
  );
};

// Guard that redirects patients away from professional-only routes
export const ProfessionalOnly = ({ children }) => {
  const { user } = useAuth();
  if (user?.role === 'patient') return <Navigate to="/telemedicine" replace />;
  return children;
};

// Listen for messages from the service worker (e.g. notification click deep-links)
export const ServiceWorkerBridge = () => {
  const navigate = useNavigate();
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const onMessage = (event) => {
      const data = event?.data;
      if (data && data.type === 'navigate' && typeof data.url === 'string') {
        navigate(data.url);
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);
  return null;
};
